import { type ReactNode, useMemo } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Badge, Text } from '@/components/typography';
import { cn } from '@/lib/utils';

import { useLegendContext } from './Legend';

type Props = {
  className?: string;
  value: string;
  name?: string;
  color?: string | null;
  noToggleVisibility?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
};

const LegendItem = ({
  className = '',
  value,
  color,
  name,
  noToggleVisibility: noToggleVisibilityProp,
  onChange,
  ...rest
}: Props) => {
  const { disabledSeriesValues, noToggleVisibility, onToggleSeries } = useLegendContext();
  const interactive =
    noToggleVisibilityProp == null ? !noToggleVisibility : !noToggleVisibilityProp;
  const selected = !disabledSeriesValues?.includes(value);

  const handleChange = (isChecked: boolean) => {
    onChange?.(isChecked);
    onToggleSeries?.(value, isChecked);
  };

  const children = useMemo<ReactNode>(() => {
    return (
      <Tooltip disabled={!name}>
        <TooltipTrigger
          delay={1750}
          render={
            <span className={cn('flex items-center gap-2', !selected && 'opacity-50')}>
              {color && (
                <div
                  className="size-3.5 min-h-3.5 min-w-3.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
              )}
              <Text
                className={cn('overflow-hidden text-ellipsis', !selected && 'line-through')}
                variant="caption1"
              >
                {name}
              </Text>
            </span>
          }
        />
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    );
  }, [color, name, selected]);

  if (interactive) {
    const { disabled, ...badgeRest } = rest;
    return (
      <Badge
        {...badgeRest}
        disabled={disabled}
        render={<button type="button" disabled={disabled} />}
        showTooltip={false}
        size="small"
        className={cn(
          'flex! h-7.5! max-w-1/2! items-center! gap-2! px-1.5! whitespace-nowrap!',
          'me-0! cursor-pointer! rounded! border-1.5! border-gray-400! bg-transparent!',
          'hover:bg-gray-300! focus:bg-gray-300! focus-visible:ring-0! active:bg-gray-300!',
          className,
        )}
        onClick={disabled ? undefined : () => handleChange(!selected)}
        aria-pressed={selected}
      >
        {children}
      </Badge>
    );
  }

  return (
    <div
      className={cn(
        'flex h-7.5 max-w-[50%] items-center gap-2 px-1.5 whitespace-nowrap',
        className,
      )}
    >
      {children}
    </div>
  );
};

export default LegendItem;
